import { lazy, Suspense, useEffect } from "react";
import { Router, Switch, Route, useLocation } from "wouter";
import { Cursor } from "./components/Cursor";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { CookieBanner } from "./components/CookieBanner";
import { InstagramFeed } from "./components/InstagramFeed";
import { Seo } from "./components/Seo";
import { Home } from "./pages/Home";

const AboutPage = lazy(() =>
  import("./pages/AboutPage").then((m) => ({ default: m.AboutPage })),
);
const BrandStand = lazy(() =>
  import("./pages/BrandStand").then((m) => ({ default: m.BrandStand })),
);
const CreativeMarketing = lazy(() =>
  import("./pages/CreativeMarketing").then((m) => ({
    default: m.CreativeMarketing,
  })),
);
const LegacyForge = lazy(() =>
  import("./pages/LegacyForge").then((m) => ({ default: m.LegacyForge })),
);
const DeNovoSolutions = lazy(() =>
  import("./pages/DeNovoSolutions").then((m) => ({
    default: m.DeNovoSolutions,
  })),
);
const SuccessStories = lazy(() =>
  import("./pages/SuccessStories").then((m) => ({
    default: m.SuccessStories,
  })),
);
const Work = lazy(() =>
  import("./pages/Work").then((m) => ({ default: m.Work })),
);
const WorkDetail = lazy(() =>
  import("./pages/WorkDetail").then((m) => ({ default: m.WorkDetail })),
);
const News = lazy(() =>
  import("./pages/News").then((m) => ({ default: m.News })),
);
const NewsDetail = lazy(() =>
  import("./pages/NewsDetail").then((m) => ({ default: m.NewsDetail })),
);
const IndieFilm = lazy(() =>
  import("./pages/IndieFilm").then((m) => ({ default: m.IndieFilm })),
);
const MemoryWall = lazy(() =>
  import("./pages/MemoryWall").then((m) => ({ default: m.MemoryWall })),
);
const VerticalMicroDramas = lazy(() =>
  import("./pages/VerticalMicroDramas").then((m) => ({
    default: m.VerticalMicroDramas,
  })),
);
const Contact = lazy(() =>
  import("./pages/Contact").then((m) => ({ default: m.Contact })),
);
const TermsAndConditions = lazy(() =>
  import("./pages/TermsAndConditions").then((m) => ({
    default: m.TermsAndConditions,
  })),
);
const TermsOfSupply = lazy(() =>
  import("./pages/TermsOfSupply").then((m) => ({ default: m.TermsOfSupply })),
);
const PrivacyPolicy = lazy(() =>
  import("./pages/PrivacyPolicy").then((m) => ({ default: m.PrivacyPolicy })),
);

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    const pending = sessionStorage.getItem("pendingScrollTarget");
    if (location === "/" && pending) {
      sessionStorage.removeItem("pendingScrollTarget");
      requestAnimationFrame(() => {
        const element = document.querySelector(pending);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0 });
        }
      });
      return;
    }
    window.scrollTo({ top: 0 });
  }, [location]);

  return null;
}

function App() {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");

  return (
    <Router base={base}>
      <ScrollToTop />
      <Seo />
      <div className="bg-black min-h-screen text-white selection:bg-primary selection:text-white">
        <Cursor />
        <Navbar />
        <Suspense fallback={<div className="min-h-screen bg-black" />}>
          <Switch>
            <Route path="/" component={Home} />
            <Route path="/about" component={AboutPage} />
            <Route path="/brand-stand" component={BrandStand} />
            <Route path="/creative-marketing" component={CreativeMarketing} />
            <Route path="/legacy-forge" component={LegacyForge} />
            <Route path="/de-novo-solutions" component={DeNovoSolutions} />
            <Route path="/success-stories" component={SuccessStories} />
            <Route path="/work" component={Work} />
            <Route path="/work/:slug" component={WorkDetail} />
            <Route path="/news" component={News} />
            <Route path="/news/:slug" component={NewsDetail} />
            <Route path="/indie-film" component={IndieFilm} />
            <Route
              path="/vertical-micro-dramas"
              component={VerticalMicroDramas}
            />
            <Route path="/memory-wall" component={MemoryWall} />
            <Route path="/contact" component={Contact} />
            <Route
              path="/terms-and-conditions"
              component={TermsAndConditions}
            />
            <Route
              path="/terms-and-conditions-supply-of-services"
              component={TermsOfSupply}
            />
            <Route path="/privacy-policy" component={PrivacyPolicy} />
          </Switch>
        </Suspense>
        <InstagramFeed />
        <Footer />
        <CookieBanner />
      </div>
    </Router>
  );
}

export default App;
